import {z} from 'zod';

const HIGHLIGHT_DURATION_EPSILON = 1e-6;

export const HighlightTypeSchema = z.enum([
  'rectangle',
  'circle',
  'arrow',
  'focus-area',
  'freeze',
]);

export const positionSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number().positive('宽度必须大于 0'),
  height: z.number().positive('高度必须大于 0'),
  unit: z.enum(['px', 'percent']).optional(),
});

export const highlightItemSchema = z
  .object({
    type: HighlightTypeSchema.default('rectangle'),
    startTime: z.number().min(0, '开始时间不能为负数'),
    endTime: z.number().min(0, '结束时间不能为负数').optional(),
    duration: z.number().min(0, '持续时间不能为负数').optional(),
    position: positionSchema.optional(),
    borderRadius: z.number().min(0).optional(),
    borderWidth: z.number().min(1).max(50).optional(),
    borderColor: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/, '必须是有效的十六进制颜色（如 #ff0000）')
      .optional(),
    rotation: z.number().optional(),
    fadeIn: z.number().min(0).max(5).optional(),
    fadeOut: z.number().min(0).max(5).optional(),
    zoomScale: z.number().min(1).max(5).optional(),
    autoFitZoom: z.boolean().optional(),
    easingType: z
      .enum(['linear', 'ease-in', 'ease-out', 'ease-in-out'])
      .optional(),
  })
  .superRefine((highlight, ctx) => {
    if (highlight.type !== 'freeze' && highlight.position == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['position'],
        message: '非 freeze 高亮必须提供 position',
      });
    }

    if (highlight.endTime == null && highlight.duration == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: '必须提供 duration，或提供 endTime 来确定高亮区间',
      });
      return;
    }

    if (highlight.endTime != null && highlight.endTime < highlight.startTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endTime'],
        message: '结束时间不能早于开始时间',
      });
    }

    if (
      highlight.endTime == null &&
      (highlight.duration == null || highlight.duration <= 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: '未提供 endTime 时，duration 必须大于 0',
      });
    }

    if (highlight.endTime != null && highlight.duration != null) {
      const derivedDuration = highlight.endTime - highlight.startTime;
      if (
        Math.abs(derivedDuration - highlight.duration) >
        HIGHLIGHT_DURATION_EPSILON
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['duration'],
          message:
            '同时提供 duration 和 endTime 时，两者必须表示同一个时间区间',
        });
      }
    }
  });

export const videoHighlightSchema = z.object({
  videoConfig: z
    .object({
      width: z.number().int().positive().optional(),
      height: z.number().int().positive().optional(),
      fps: z.number().int().positive().optional(),
    })
    .optional(),
  backgroundVideo: z.object({
    url: z.string().min(1),
    duration: z.number().min(0).nullable().optional(),
    width: z.number().positive().nullable().optional(),
    height: z.number().positive().nullable().optional(),
    fps: z.number().positive().nullable().optional(),
  }),
  highlights: z.array(highlightItemSchema).max(50),
  defaultFadeIn: z.number().min(0).max(5).optional().default(0),
  defaultFadeOut: z.number().min(0).max(5).optional().default(0),
  // Background video original audio volume (0-100, default 100 = full volume).
  // Mirrors VideoCommentaryHighlight.originalVolume so the upstream caller can
  // reuse the same field; the background video stays muted on the visual track
  // and the audio is emitted via a dedicated <Audio> component.
  originalVolume: z.number().min(0).max(100).optional().default(100),
});

export type HighlightType = z.infer<typeof HighlightTypeSchema>;
export type Position = z.infer<typeof positionSchema>;
export type HighlightItem = z.infer<typeof highlightItemSchema>;
export type VideoHighlightProps = Record<string, unknown> &
  z.infer<typeof videoHighlightSchema>;
